import React from 'react';
import { Link } from 'react-router-dom';

function Overview() {
  return (
    <div className="docs-content">
      <h1>SignTada Documentation</h1>

      <h2>Product overview</h2>
      <p>
        SignTada is a secure electronic signature platform. You can send document signing requests,
        use reusable templates, collect e-signatures (and optional OTP, location, or face capture),
        and download a legal audit trail for each document. The dashboard shows documents, stats,
        templates, and webhooks. Access is role-based: User, Admin, and Super Admin.
      </p>

      <h2>Getting started</h2>
      <p>To use SignTada:</p>
      <ol>
        <li><strong>Log in</strong> — Use email/password or Google sign-in. The first user in the system becomes Super Admin; others are invited by an Admin or Super Admin.</li>
        <li><strong>Dashboard</strong> — After login you see the main dashboard: overview stats, documents, new request, templates, and webhooks (depending on your role).</li>
        <li><strong>First steps</strong> — Send a document via <strong>New Request</strong> (upload a PDF and add recipients), or create a <strong>Template</strong> for repeated use.</li>
      </ol>
      <p>For a step-by-step walkthrough, see <Link to="/quick-start">Quick Start</Link>. For deeper dives, use the sidebar: <Link to="/user-roles">User Roles</Link>, <Link to="/dashboard">Dashboard Guide</Link>, <Link to="/signing-flow">Signing Flow</Link>, <Link to="/verification">Document Verification</Link>, <Link to="/branding">Branding</Link>, and <Link to="/api-keys">API Keys</Link>.
      </p>

      <h2>User roles</h2>
      <p>Three roles control what you can do:</p>
      <ul>
        <li><strong>USER</strong> — View dashboard, send document requests, use templates, view documents and signing status. Cannot manage webhooks, users, or company settings.</li>
        <li><strong>ADMIN</strong> — Everything a User can do, plus: manage webhooks, add users to their company, and configure branding (logo, theme, colors) for their company in Settings.</li>
        <li><strong>SUPER_ADMIN</strong> — Full access: create companies, add credits to any company, switch the dashboard view between “All Companies” and a specific company, manage all users, and configure branding per company.</li>
      </ul>
      <p>Credits are consumed per signing request. Admins see their company’s credits; Super Admins can add credits to companies from Settings.</p>

      <h2>Dashboard</h2>
      <p>From the sidebar you can open:</p>
      <ul>
        <li><strong>Dashboard (Overview)</strong> — Stats (total, signed, pending, completion rate), charts, recent activity, top recipients, and quick actions (New Request, Templates). Credits are shown here.</li>
        <li><strong>Documents</strong> — List of all document requests with filters (All, Signed, Pending, Rejected) and pagination. Each row shows document name, status, and recipient signing status.</li>
        <li><strong>New Request</strong> — Start a new signing request: upload a file (or use a template), add recipients, set signature positions and security options (email notification, OTP, expiry, etc.).</li>
        <li><strong>Templates</strong> — Create, edit, or delete templates. Templates define a base PDF plus signature and text field positions and roles; they can be used when creating requests.</li>
        <li><strong>Webhooks</strong> — Configure a webhook URL and optional headers for event notifications. View delivery logs and resend failed deliveries (rate-limited). Admins and Super Admins only.</li>
      </ul>
      <p>Super Admins see a company switcher in the top bar (“Viewing: All Companies” or a specific company); stats and data then apply to the selected scope.</p>

      <h2>Signing flow</h2>
      <ol>
        <li><strong>Sender</strong> creates a request from the dashboard (New Request or via API), adds one or more recipients, and optionally enables OTP, location capture, or face capture.</li>
        <li><strong>Recipient</strong> receives a signing link (by email or shared URL), opens it, and is taken to the sign page. They can sign or reject. If OTP is enabled, they must verify with a code sent to their phone/email.</li>
        <li><strong>After signing</strong> — The document is updated; the sender can download the signed PDF and the audit trail. Webhooks fire for events (e.g. request created, document signed).</li>
      </ol>
      <p>Each signing action is recorded in the audit trail (IP, device, timestamp, optional location/face). Use the <strong>Download audit</strong> option or the API to get the audit PDF.</p>

      <h2>Verify document</h2>
      <p>
        The public <strong>Verify</strong> page (e.g. <code>/verify</code>) lets anyone with the document reference check that a document was signed through SignTada. No login required. You can link to it from your app or emails for transparency.
      </p>

      <h2>Branding</h2>
      <p>
        Admins and Super Admins can customize the look of the app for their organization: product name, logo, primary and accent colors, and theme (e.g. default, Ocean). This is configured under <strong>Settings</strong> in the dashboard. Super Admins can set branding per company when a company is selected.
      </p>

      <h2>Using your API key</h2>
      <p>
        For server-to-server or scripted access, get your <strong>API key</strong> from the dashboard under <strong>Settings</strong>. Include it in the <code>X-API-Key</code> header when calling protected endpoints: <code>X-API-Key: sk_live_&lt;your_key&gt;</code>. Key management (create, revoke) is done only in the dashboard, not via public API.
      </p>

      <h2>Base URL</h2>
      <p>All API requests use this base URL (replace with your deployment if different):</p>
      <pre><code>https://api.signtada.com</code></pre>
      <p>For user-based auth, send <code>Authorization: Bearer &lt;access_token&gt;</code>. For API-key auth, send <code>X-API-Key: sk_live_&lt;your_key&gt;</code>.</p>

      <h2>API Reference</h2>
      <p>Full endpoint details, request/response shapes, and try-it examples:</p>
      <ul>
        <li><Link to="/api-reference/auth">Authentication</Link> — Register, login (email/password and Google), refresh, logout, me</li>
        <li><Link to="/api-reference/requests">Document Requests</Link> — Create request, list requests, get by reference, stats, top recipients, recent activity</li>
        <li><Link to="/api-reference/templates">Templates</Link> — Create, list, get, update, delete</li>
        <li><Link to="/api-reference/webhooks">Webhooks</Link> — Get/update config, logs, resend</li>
        <li><Link to="/api-reference/files">Files</Link> — Upload, view/download by path</li>
        <li><Link to="/api-reference/signing">Signing</Link> — Sign document, reject document, download audit</li>
        <li><Link to="/api-reference/users">Users</Link> — List, create, update, delete users (Admin/Super Admin)</li>
        <li><Link to="/api-reference/otp">OTP</Link> — Send and verify OTP (e.g. for signing)</li>
        <li><Link to="/api-reference/branding">Branding</Link> — Get/update branding (Admin/Super Admin)</li>
      </ul>

      <h2>Support</h2>
      <p>
        For help, contact your administrator or support at <a href="mailto:support@signtada.com">support@signtada.com</a>. Use the dashboard for sending documents and managing templates; use the API for integrations and automation.
      </p>
    </div>
  );
}

export default Overview;
